/**
 * Air density correction for the rangefinder.
 *
 * Golf-ball carry is mostly determined by aerodynamic drag, which scales
 * with air density. Hotter air, higher altitude, or higher humidity all
 * reduce air density and let the ball fly farther.
 *
 * We use Tour-grade rules of thumb so the math stays honest without
 * pretending we model spin loft / launch angle / temperature on the ball:
 *
 *   • Temperature: +0.15 % carry per °F above 70°F baseline (TrackMan data;
 *     Bryson DeChambeau's coach Mike Carbray quotes "1 yard per 7°F", which
 *     rounds to the same number on a 150-yard shot).
 *
 *   • Altitude: +2 % carry per 1000 ft above sea level (very well established
 *     from PGA Tour Denver-area data; Coors Field is the canonical example).
 *
 *   • Humidity: skipped. Water vapour does displace heavier N2/O2 (so humid
 *     air is *slightly* less dense), but the effect is < 0.5 % even at 90%
 *     RH versus 0% RH at the same temperature. Tour pros don't adjust for it.
 *
 * Open-Meteo's free `/forecast` endpoint returns temperature_2m (°F when we
 * pass temperature_unit=fahrenheit) and we already fetch elevation via the
 * Elevation API — so we get both inputs for zero cost and zero secrets.
 */

const ENDPOINT = "https://api.open-meteo.com/v1/forecast";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min — air conditions drift slowly
const cache = new Map(); // key = "lat,lng" → { value, ts }

function key(lat, lng) { return `${lat.toFixed(3)},${lng.toFixed(3)}`; }

/** Fetch the current air temperature in °F at a point. Cached 10 min. */
export async function fetchAirTemperatureF(lat, lng) {
  if (lat == null || lng == null) return null;
  const k = key(lat, lng);
  const cached = cache.get(k);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.value;
  try {
    const url = `${ENDPOINT}?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&current=temperature_2m&temperature_unit=fahrenheit`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const t = json?.current?.temperature_2m;
    if (typeof t !== "number") return null;
    cache.set(k, { value: t, ts: Date.now() });
    return t;
  } catch { return null; }
}

const BASELINE_F = 70;
const TEMP_PCT_PER_F = 0.0015; // +0.15% carry per °F above 70°F
const ALT_PCT_PER_1000FT = 0.020; // +2% carry per 1000 ft above sea level
const METRES_TO_FEET = 3.28084;

/** Adjust a yardage for current air density (temperature + altitude).
 *  Both inputs are optional — pass `null` to skip that adjustment. */
export function airDensityAdjustedYards(yards, tempF, altitudeMetres) {
  if (yards == null) return yards;
  let pct = 0;
  if (typeof tempF === "number") pct += TEMP_PCT_PER_F * (tempF - BASELINE_F);
  if (typeof altitudeMetres === "number") pct += ALT_PCT_PER_1000FT * (altitudeMetres * METRES_TO_FEET / 1000);
  if (!pct) return yards;
  // Note the sign: warmer/higher → air is THINNER → ball flies FARTHER, so
  // the club distance needed to reach the same point is LESS. We display the
  // "plays like" number, i.e. how far the ball will actually carry on this
  // club, so a 150-yard 7-iron at 90°F plays like 154 yards.
  return Math.max(0, Math.round(yards * (1 + pct)));
}

/** Inverse — given a flat distance and the air conditions, return how far it
 *  effectively plays. Equivalent to `airDensityAdjustedYards` but kept for
 *  call-site readability (we use this when reporting "plays like" numbers). */
export const playsLikeAirDensity = airDensityAdjustedYards;

/** Pretty label for the rangefinder overlay, e.g. "82°F · 4 m" (Fiji typical)
 *  or "65°F · 5,280 ft" (Denver). Returns null if both inputs are missing. */
export function describeAirDensity(tempF, altitudeMetres) {
  const parts = [];
  if (typeof tempF === "number") parts.push(`${Math.round(tempF)}°F`);
  if (typeof altitudeMetres === "number") {
    const ft = altitudeMetres * METRES_TO_FEET;
    parts.push(ft >= 200 ? `${Math.round(ft).toLocaleString()} ft` : `${Math.round(altitudeMetres)} m`);
  }
  return parts.length ? parts.join(" · ") : null;
}
